'use strict';

/**
 * Gets the screen width
 */
function getScreenWidth() {
  return (
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth
  );
}

/**
 * Listen for scroll event and execute functions.
 * We should use this function instead of scroll event
 * for the sake of site performance.
 *
 * @param {FrameRequestCallback} cb Callback
 * @returns {['scroll', Function]} a pair event name and listener to destroy if needed
 */
function onScroll(cb) {
  let lastPosition = 0;
  let skipNext = false;

  const listener = () => {
    if (skipNext) return;

    window.requestAnimationFrame(() => {
      cb(window.scrollY, lastPosition);

      lastPosition = window.scrollY;
      skipNext = false;
    });

    skipNext = true;
  };

  document.addEventListener('scroll', listener);
  return ['scroll', listener];
}

/**
 * Initialize Menu
 *
 * @param {string} selector CSS selector
 */

function initMenu(selector) {
  function setMenuOnClick(parentMenu) {
    parentMenu.addEventListener('click', function () {
      parentMenu.classList.toggle('active');
    });
    window.addEventListener('click', function (e) {
      const target = e.target;
      if (!parentMenu.contains(target)) {
        parentMenu.classList.remove('active');
      }
    });
    parentMenu.querySelectorAll('a:not([href="#"])').forEach(item => {
      item.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    });
  }

  function setMenuStateWhen(parentMenu) {
    const isNotLiElement = !(parentMenu instanceof HTMLLIElement);
    if (isNotLiElement) return;
    setMenuOnClick(parentMenu);
    // THE DESKTOP BEHAVIOR IS HANDLING WITH CSS
  }

  const menuHasChildren = document
    .querySelector(selector)
    .querySelectorAll('.menu-item-has-children');
  menuHasChildren.forEach(parentMenu => {
    setMenuStateWhen(parentMenu);
  });

  document
    .querySelector('.menu-mobile-btn')
    .addEventListener('click', function () {
      this.classList.toggle('toggled');
      document.querySelector(selector).classList.toggle('show');
      document.body.classList.toggle('crop');
    });

  document
    .querySelector(selector)
    .querySelectorAll('a[href="#"]')
    .forEach(item => {
      item.addEventListener('click', function (e) {
        e.preventDefault();
      });
    });
}

/**
 * Initialize Safe Area Menu
 * This function is used to create a safe area for the menu item with children
 * to prevent the menu from closing when the mouse moves to the submenu.
 */
function initializeSafeAreaMenu() {
  const menuItems = document.querySelectorAll(
    '.header-menu .menu-item-has-children'
  );

  menuItems.forEach(menuItem => {
    const megaMenu = menuItem.querySelector('.sub-menu');
    if (!megaMenu) return;

    let safeAreaSvg = null;
    let isMouseOverMenu = false;
    let isOpen = false;
    let menuTimeout;

    // Track mouse position for the dynamic triangle
    let mouseX = 0;
    let mouseY = 0;

    document.addEventListener('mousemove', e => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      // Update the safe area if it exists and menu is open
      if (safeAreaSvg && megaMenu.style.visibility === 'visible') {
        updateSafeArea();
      }
    });

    // Open menu on mouse enter
    menuItem.addEventListener('mouseenter', () => {
      clearTimeout(menuTimeout);
      if (!isOpen) {
        isOpen = true;
        megaMenu.style.visibility = 'visible';
        createSafeArea();
      }
    });

    // Handle mouse leave with proper checks for safe area
    menuItem.addEventListener('mouseleave', e => {
      // Check if the relatedTarget (element mouse went to) is the safe area path
      const isSafeArea =
        e.relatedTarget &&
        (e.relatedTarget.tagName === 'path' ||
          e.relatedTarget.closest('#svg-safe-area'));

      if (isSafeArea) {
        // Mouse went to safe area, don't close the menu
        return;
      }

      menuTimeout = setTimeout(() => {
        if (!isMouseOverMenu) {
          closeSafeArea();
          megaMenu.style.visibility = 'hidden';
          isOpen = false;
        }
      }, 100);
    });

    // Track when mouse enters the mega menu
    megaMenu.addEventListener('mouseenter', () => {
      clearTimeout(menuTimeout);
      isMouseOverMenu = true;
    });

    // Track when mouse leaves the mega menu
    megaMenu.addEventListener('mouseleave', () => {
      isMouseOverMenu = false;

      menuTimeout = setTimeout(() => {
        if (!menuItem.matches(':hover')) {
          closeSafeArea();
          megaMenu.style.visibility = 'hidden';
          isOpen = false;
        }
      }, 100);
    });

    function createSafeArea() {
      // Remove any existing safe area
      closeSafeArea();

      // Create the SVG element
      safeAreaSvg = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );
      safeAreaSvg.id = 'svg-safe-area';
      safeAreaSvg.style.position = 'fixed';
      safeAreaSvg.style.pointerEvents = 'none'; // SVG container doesn't capture events
      safeAreaSvg.style.zIndex = '9999'; // Between menu item and mega menu

      // Create the path with event listeners to track when mouse is over it
      const path = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path'
      );
      path.style.pointerEvents = 'auto'; // Allow mouse events on path

      // Track when mouse is over the safe area path
      path.addEventListener('mouseenter', () => {
        // Mouse entered safe area
      });

      path.addEventListener('mouseleave', e => {
        // Only close if not going to menu item or mega menu
        const relatedTarget = e.relatedTarget;
        const isGoingToMenuOrSubmenu =
          relatedTarget === menuItem ||
          relatedTarget === megaMenu ||
          (relatedTarget &&
            (relatedTarget.closest('.menu-item-has-children') ||
              relatedTarget.closest('.sub-menu')));

        if (!isGoingToMenuOrSubmenu) {
          menuTimeout = setTimeout(() => {
            closeSafeArea();
            megaMenu.style.visibility = 'hidden';
            isOpen = false;
          }, 100);
        }
      });

      safeAreaSvg.appendChild(path);
      document.body.appendChild(safeAreaSvg);

      // Initial update of the safe area
      updateSafeArea();
    }

    function updateSafeArea() {
      if (!safeAreaSvg) return;

      const menuRect = menuItem.getBoundingClientRect();
      const megaMenuRect = megaMenu.getBoundingClientRect();

      // The safe area will be a dynamic triangle based on mouse position
      // Calculate SVG dimensions and position
      const svgLeft = Math.min(menuRect.left, megaMenuRect.left);
      const svgWidth = Math.max(menuRect.right, megaMenuRect.right) - svgLeft;
      const svgTop = menuRect.bottom;
      const svgHeight = megaMenuRect.top - menuRect.bottom;

      // Position the SVG
      safeAreaSvg.style.width = `${svgWidth}px`;
      safeAreaSvg.style.height = `${svgHeight}px`;
      safeAreaSvg.style.top = `${svgTop}px`;
      safeAreaSvg.style.left = `${svgLeft}px`;

      // Get relative mouse position within the menu item
      const relativeMouseX = mouseX - svgLeft;

      // Create a triangle path from mouse position to full mega menu width
      const path = safeAreaSvg.querySelector('path');
      if (path) {
        path.setAttribute(
          'd',
          `
          M ${relativeMouseX}, 0
          L ${megaMenuRect.right - svgLeft}, ${svgHeight}
          L ${megaMenuRect.left - svgLeft}, ${svgHeight}
          Z
        `
        );
      }
    }

    function closeSafeArea() {
      if (safeAreaSvg) {
        document.body.removeChild(safeAreaSvg);
        safeAreaSvg = null;
      }
    }
  });
}

/**
 * ...
 *
 * @param {string} id
 * @param {number} durationSecond
 * @param {number} fps
 * @returns
 */
function initTopBtn(id) {
  const btn = document.querySelector(id);
  if (!btn) return;
  btn.addEventListener('click', scrollToTop);
  onScroll((position, lastPosition) => {
    const isShown = btn.classList.contains('show');

    if (position > 100 && position < lastPosition) {
      if (isShown) return;
      btn.classList.add('show');
    }

    if (!isShown) return;
    btn.classList.remove('show');
  });
}

function scrollToTop() {
  window.scroll({
    top: 0,
    left: 0,
  });
}

function initAnimations() {
  const elements = document.querySelectorAll(
    '.element-animate-self, .container-animate'
  );

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.intersectionRatio >= 0.1) {
          entry.target.classList.add('is-show');
          obs.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
    }
  );

  elements.forEach(element => {
    observer.observe(element);
  });
}

function checkAdminBar() {
  const adminBar = document.querySelector('#wpadminbar');
  const adminBarHeight = adminBar ? adminBar.offsetHeight : 0;
  document.documentElement.style.setProperty(
    '--wp-admin-bar-height',
    adminBarHeight + 'px'
  );
}

function updateViewHeight() {
  let vh =
    Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    ) * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

document.addEventListener('DOMContentLoaded', function () {
  checkAdminBar();
  updateViewHeight();
  initMenu('.header-nav');
  // initializeSafeAreaMenu();
  initTopBtn('#scroll-to-top');
  initAnimations();
  document.querySelector('#loader-temp').remove();
});
